import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Card, Empty, List, Result, Switch, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import { LoadProblem } from "../components/LoadProblem";
import { PageHeader } from "../components/PageHeader";

interface FeatureOption {
  code: string;
  name: string;
  group: string;
  group_label: string;
  enabled: boolean;
  description?: string | null;
  /** true = the toggle changes behaviour in this version; false = stored for later, no effect yet */
  wired: boolean;
}

export function SettingsPage() {
  const { can, loadMe } = useAuthStore();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const canRead = can("core.settings.read");
  const canWrite = can("core.settings.write");
  // BUG-R31-001: four states said apart — no right, loading, a failed load with «Повторить», an empty answer
  const options = useQuery({
    queryKey: ["feature-options"],
    queryFn: () => api.get<FeatureOption[]>("/feature-options"),
    enabled: canRead,
    retry: false,
  });
  const toggle = useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) =>
      api.patch<FeatureOption>(`/feature-options/${code}`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-options"] });
      queryClient.invalidateQueries({ queryKey: ["meta"] });
      loadMe().catch(() => undefined);
    },
    onError: (e) => message.error(errorMessage(e)),
  });

  const groups = new Map<string, FeatureOption[]>();
  for (const option of options.data ?? []) {
    groups.set(option.group_label, [...(groups.get(option.group_label) ?? []), option]);
  }

  if (!canRead) {
    return (
      <div style={{ maxWidth: 900 }}>
        <PageHeader title="Настройки системы" />
        <Result
          status="403"
          title="Нет доступа к настройкам системы"
          subTitle="Настройки системы смотрит и меняет администратор. Если вам нужно изменить опцию, обратитесь к администратору."
          extra={
            <Link to="/dashboard">
              <Typography.Link>На рабочий стол</Typography.Link>
            </Link>
          }
        />
      </div>
    );
  }
  if (options.isError) {
    return (
      <div style={{ maxWidth: 900 }}>
        <PageHeader title="Настройки системы" />
        <LoadProblem error={options.error} onRetry={() => options.refetch()} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <PageHeader title="Настройки системы" subtitle="Функциональные опции: включайте только то, что действительно используете" />
      {!canWrite ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Только просмотр"
          description="У вашей роли нет права изменять настройки: переключатели показывают текущие значения и не меняются. Изменения вносит администратор."
        />
      ) : null}
      {options.isLoading ? <Card loading style={{ marginBottom: 16 }} /> : null}
      {options.isSuccess && (options.data ?? []).length === 0 ? (
        <Empty description="Функциональных опций нет: настраивать в этой версии нечего" />
      ) : null}
      <Typography.Paragraph type="secondary">
        Функциональные опции включают и выключают блоки системы. Зелёная метка «работает» означает, что переключатель уже влияет на поведение. Серые опции перенесены из исходной системы и пока не реализованы: включить их нельзя, чтобы не создавать впечатление, что ограничение уже действует.
      </Typography.Paragraph>
      {[...groups.entries()].map(([group, items]) => (
        <Card key={group} title={group} style={{ marginBottom: 16 }} loading={options.isLoading}>
          <List
            dataSource={items}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Switch
                    key="switch"
                    checked={item.enabled}
                    disabled={!canWrite || (!item.wired && !item.enabled)}
                    loading={toggle.isPending && toggle.variables?.code === item.code}
                    onChange={(enabled) => toggle.mutate({ code: item.code, enabled })}
                  />,
                ]}
              >
                <List.Item.Meta
                  title={
                    <span>
                      {item.name}{" "}
                      {item.wired ? <Tag color="green">работает</Tag> : item.enabled ? <Tag color="orange">включена, но пока не влияет</Tag> : <Tag>в плане, включить нельзя</Tag>}
                    </span>
                  }
                  description={item.description ?? "Модуль ещё не реализован: значение сохраняется и заработает вместе с ним"}
                />
              </List.Item>
            )}
          />
        </Card>
      ))}
    </div>
  );
}
